import React, { useCallback } from "react";
import { Form, Label, Icon } from "semantic-ui-react";
import { useDropzone } from "react-dropzone";
import { useFormik } from "formik";
import { InstitutionalDocument } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { buildStorageUrl, showError } from "../../../../utils";
import { initialValues, validationSchema } from "./InstitutionalDocumentForm.form";
import "./InstitutionalDocumentForm.scss";

const institutionalDocumentController = new InstitutionalDocument();

export function InstitutionalDocumentForm(props) {
  const { onClose, onReload, document } = props;
  const { accessToken } = useAuth();

  const formik = useFormik({
    initialValues: initialValues(document),
    validationSchema: validationSchema(document),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        if (document) {
          // PATCH: solo campos modificados + archivo si fue cambiado.
          const data = {};
          if (formValue.title !== document.title) data.title = formValue.title;
          if (Number(formValue.year) !== document.year) data.year = formValue.year;
          if ((formValue.description || "") !== (document.description || ""))
            data.description = formValue.description;
          if (formValue.file) data.file = formValue.file;

          await institutionalDocumentController.updateInstitutionalDocument(
            accessToken,
            document.id,
            data
          );
        } else {
          await institutionalDocumentController.createInstitutionalDocument(
            accessToken,
            {
              title: formValue.title,
              year: formValue.year,
              description: formValue.description,
              file: formValue.file,
            }
          );
        }
        onReload();
        onClose();
      } catch (error) {
        showError(
          "No se pudo guardar el documento",
          error?.message || "El archivo debe ser un PDF o faltan datos obligatorios."
        );
      }
    },
  });

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) formik.setFieldValue("file", file);
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: "application/pdf",
    multiple: false,
    onDrop,
  });

  const currentFileUrl = document?.filePath
    ? buildStorageUrl(document.filePath)
    : null;

  return (
    <Form className="institutional-document-form" onSubmit={formik.handleSubmit}>
      <Form.Input
        name="title"
        label="Título"
        placeholder="Ej: Plan Anual de Trabajo"
        onChange={formik.handleChange}
        value={formik.values.title}
        error={formik.errors.title}
      />
      <Form.Input
        type="number"
        name="year"
        label="Año"
        placeholder="Ej: 2026"
        onChange={formik.handleChange}
        value={formik.values.year}
        error={formik.errors.year}
      />
      <Form.TextArea
        name="description"
        label="Descripción (opcional)"
        placeholder="Descripción del documento"
        onChange={formik.handleChange}
        value={formik.values.description}
        error={formik.errors.description}
      />

      <div className="institutional-document-form__field">
        <label className="institutional-document-form__label">Archivo PDF</label>

        {document && currentFileUrl && (
          <div className="institutional-document-form__current">
            <Icon name="file pdf outline" color="red" />
            <span>PDF actual: </span>
            <a href={currentFileUrl} target="_blank" rel="noopener noreferrer">
              {document.fileName}
            </a>
          </div>
        )}

        <div
          className={`institutional-document-form__dropzone ${
            isDragActive ? "active" : ""
          }`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Icon name="cloud upload" size="large" />
          {formik.values.file ? (
            <p className="institutional-document-form__filename">
              <Icon name="file pdf outline" color="red" />
              {formik.values.file.name}
            </p>
          ) : (
            <p>
              {document
                ? "Arrastra un nuevo PDF o haz clic para reemplazar el actual"
                : "Arrastra el PDF aquí o haz clic para seleccionarlo"}
            </p>
          )}
        </div>

        {formik.errors.file && (
          <Label
            prompt
            basic
            pointing
            className="institutional-document-form__error"
          >
            {formik.errors.file}
          </Label>
        )}
      </div>

      <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
        {document ? "Actualizar documento" : "Guardar documento"}
      </Form.Button>
    </Form>
  );
}
